// components/otherpages/QrGenrater.tsx
"use client";

import { useState, useRef } from "react";
import { m } from "framer-motion";

export const QrCodeGenerator = () => {
  const [inputValue, setInputValue] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const qrRef = useRef<HTMLDivElement>(null);

  // URL VALIDATION FUNCTION (ADDED)
  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const generateQR = async () => {
    if (!inputValue.trim() || !isValidUrl(inputValue)) {
      setError("Please enter a valid URL only!");
      return;
    }

    setError("");
    setLoading(true);
    try {
      // Lazy-load heavy libs only on generate — keeps them off the route's initial bundle.
      const [{ default: QRCode }, { default: jsPDF }] = await Promise.all([
        import("qrcode"),
        import("jspdf"),
      ]);
      const canvas = document.createElement("canvas");
      await QRCode.toCanvas(canvas, inputValue, {
        width: 400,
        margin: 4,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      });

      const dataUrl = canvas.toDataURL("image/png");
      setQrUrl(dataUrl);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [500, 700],
      });

      pdf.setFillColor(249, 250, 255);
      pdf.rect(0, 0, 500, 700, "F");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(28);
      pdf.setTextColor(90, 40, 200);
      pdf.text("Your QR Code", 250, 80, { align: "center" });

      pdf.setFontSize(16);
      pdf.setTextColor(80, 80, 80);
      pdf.text("Scan to visit:", 250, 110, { align: "center" });

      const shortText =
        inputValue.length > 50
          ? inputValue.substring(0, 47) + "..."
          : inputValue;
      pdf.setFontSize(14);
      pdf.setTextColor(100, 100, 255);
      pdf.text(shortText, 250, 135, { align: "center" });

      pdf.addImage(dataUrl, "PNG", 50, 180, 400, 400);

      pdf.setFontSize(12);
      pdf.setTextColor(150, 150, 150);
      pdf.text("Generated by Vilva Business QR Generator", 250, 650, {
        align: "center",
      });
      pdf.text("https://www.vilvabusiness.com", 250, 670, { align: "center" });

      pdf.save("Vilva-QR-Code.pdf");
    } catch (err) {
      console.error(err);
      setError("Failed to generate QR code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-12 px-4">
      <m.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-2xl mx-auto"
      >
        <div className="text-center mb-5 md:mb-10">
          <h1 className="text-[24px] md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-[#195aff] bg-clip-text text-transparent">
            QR Code Generator
          </h1>
          <p className="mt-4 text-base md:text-lg text-gray-600">
            Create custom QR codes instantly — with auto PDF download
          </p>
        </div>

        <m.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border border-purple-100"
        >
          <div className="space-y-6">
            {/* INPUT BOX WITH URL VALIDATION */}
            <div>
              <label htmlFor="qr-url" className="block text-sm font-roboto text-gray-700 mb-2">
                Enter URL
              </label>
              <input
                id="qr-url"
                type="text"
                aria-invalid={!!inputValue && !isValidUrl(inputValue)}
                aria-describedby={error ? "qr-error" : undefined}
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); if (error) setError(""); }}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  inputValue &&
                  isValidUrl(inputValue) &&
                  generateQR()
                }
                placeholder="https://www.vilvabusiness.com"
                className={`w-full px-5 py-4 rounded-xl border outline-none transition ${
                  inputValue && !isValidUrl(inputValue)
                    ? "border-red-500 focus:ring-red-100"
                    : "border-gray-300 focus:border-purple-500 focus:ring-purple-100"
                }`}
              />
              <p id="qr-error" role="alert" aria-live="assertive" className="mt-2 min-h-[20px] text-sm text-red-600">
                {error}
              </p>
            </div>

            {/* BUTTON WITH URL VALIDATION */}
            <m.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={generateQR}
              disabled={loading || !inputValue || !isValidUrl(inputValue)}
              className={`w-full py-4 rounded-xl font-semibold text-white shadow-lg transition-all ${
                loading || !inputValue || !isValidUrl(inputValue)
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-purple-600 to-[#195aff] hover:from-[#195aff] hover:to-indigo-700 shadow-purple-300"
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Generating QR & PDF...
                </span>
              ) : (
                "Generate QR Code + Download PDF"
              )}
            </m.button>
          </div>

          {qrUrl && (
            <m.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="mt-10 text-center"
            >
              <div className="inline-block bg-white rounded-2xl border border-gray-100">
                <img
                  src={qrUrl}
                  alt="Generated QR Code"
                  className="w-64 h-64 mx-auto border-8 border-gray-50 rounded-xl shadow-md"
                />
                <p className="my-6 text-sm text-gray-500">
                  PDF downloaded automatically!
                </p>
              </div>
            </m.div>
          )}
        </m.div>

        <p className="text-center mt-10 text-gray-500 text-sm">
          Powered by{" "}
          <a
            href="https://www.vilvabusiness.com"
            className="text-[#195aff] font-medium hover:underline"
          >
            Vilva Business
          </a>
        </p>
      </m.div>
    </div>
  );
};
